import { clientInfo } from 'configureBrand'
import type {
  ApiHost as TransmitApiHost,
  AssetHost as TransmitAssetHost,
  SdkEnv as TransmitSdkEnv
} from 'syf-transmit-mfe/dist/typings/transmit'
import type { YesNo } from 'typings/util'
import type { ApiHost } from '../typings/config'

type ENV = 'development' | 'production'
const { NODE_ENV } = process.env
const CLIENT_ID = process.env.REACT_APP_CLIENT

const APP_ENV = 'CF_APP_ENV' as ENV

export const publicUrlExtension = (
  hostname: string,
  clientId: ClientKeys
): string => {
  const isDevApp = /dev.synchronycredit.com/.test(hostname)

  if (isDevApp) {
    return `/react-apps/commercial/${clientId}/`
  }

  /*
   * checks for the old PCF reverse proxy. Remove these conditions once the new
   * reverse proxy is stable for QA and UAT.
   */
  const isTestApp = /\.*\.synchronycredit.com/.test(hostname)
  if (isTestApp) {
    return `/commercial/${clientId}/`
  }

  // check for prod pcf url
  const isPcfProd = /\.*\.pcf.syfbank.com/.test(hostname)
  if (isPcfProd) {
    return `/${clientId}/`
  }

  // all others (prod)
  return '/commercial'
}

interface Config {
  /**
   * Flag to control runtime application configuration overrides. DO NOT ENABLE
   * IN PROD, UAT, or QA! Set to 'yes' to allow overrides.
   */
  ALLOW_OVERRIDES: 'CF_ALLOW_OVERRIDES' | YesNo
  API_HOST: ApiHost
  /**
   * Flag to control the card activation page. Necessary to satisfy requirement
   * to deploy test page on prod without actually making the full UX active
   * until all dependencies are released to prod.
   */
  cardActivation: boolean
  DESIGN_API_HOST: string
  BASENAME: string
  // if it's true user will see the 1071 mfe
  cli1071: boolean
  APIGEE_CLIENT_ID: string
  CLIENT_ID: string
  NODE_ENV: ENV | 'test'
  ASSET_HOST: string
  SANDBOX_HOST?: string
  ALLOW_MSW: 'CF_ALLOW_MSW' | YesNo
  /**
   * Configure the transmit api host as directed by Skyfall team
   */
  transmitApiHost: TransmitApiHost
  /**
   * Configure the transmit asset host. Consumed by Transmit MFE for fetching
   * images.
   */
  transmitAssetHost: TransmitAssetHost
  /**
   * Should be 'dev' for all environments except production
   */
  transmitEnv: TransmitSdkEnv
  /**
   * Flag toggles the Workstation CLI Demographics questionnaire across all
   * clients.
   */
  workstationCli: boolean
}

const CARD_ACTIVATION = 'CF_CARD_ACTIVATION' as unknown as CardActivationFeature
const CLI1071 = 'CLI_1071' as unknown as FeatureFlag
const WORKSTATION_CLI = 'CF_WORKSTATION_CLI' as unknown as FeatureFlag

const defaultConfig: Config = {
  API_HOST: 'CF_API_HOST' as ApiHost,
  cardActivation: CARD_ACTIVATION === 'true',
  cli1071: CLI1071 === 'true',
  DESIGN_API_HOST: 'CF_DESIGN_API_HOST',
  BASENAME: publicUrlExtension(window.location.hostname, CLIENT_ID),
  APIGEE_CLIENT_ID: clientInfo.apigeeClientId[APP_ENV],
  CLIENT_ID,
  NODE_ENV,
  ASSET_HOST: 'CF_ASSET_HOST',
  ALLOW_OVERRIDES: 'CF_ALLOW_OVERRIDES',
  ALLOW_MSW: 'CF_ALLOW_MSW',
  transmitApiHost: 'CF_TRANSMIT_API_HOST' as TransmitApiHost,
  transmitAssetHost: 'CF_TRANSMIT_ASSET_HOST' as TransmitAssetHost,
  transmitEnv: 'CF_TRANSMIT_ENV' as TransmitSdkEnv,
  workstationCli: WORKSTATION_CLI === 'true'
}

const localConfig: Config = {
  API_HOST: process.env.REACT_APP_API_HOST as ApiHost,
  cardActivation: process.env.REACT_APP_CARD_ACTIVATION === 'true',
  cli1071: process.env.REACT_APP_CLI_1071 === 'true',
  DESIGN_API_HOST: process.env.REACT_APP_DESIGN_API_HOST,
  BASENAME: '',
  APIGEE_CLIENT_ID: clientInfo.apigeeClientId.development,
  CLIENT_ID,
  NODE_ENV,
  ASSET_HOST: process.env.REACT_APP_ASSET_HOST,
  SANDBOX_HOST: 'https://commercial-servicing.getsandbox.com',
  ALLOW_OVERRIDES: 'yes',
  ALLOW_MSW: process.env.REACT_APP_ALLOW_MSW,
  transmitApiHost: 'https://eisaaas-qa.glb.syfbank.com',
  transmitAssetHost: 'https://qa.synchronycredit.com/assets',
  transmitEnv: 'dev',
  workstationCli: process.env.REACT_APP_WORKSTATION_CLI === 'true'
} as const

export default NODE_ENV === 'production' ? defaultConfig : localConfig
